import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useRef, useState } from 'react';

import type { PaginationSort } from '@models';
import { api } from '@store/api';
import { useAppDispatch } from '@store/hooks';
import { hasActiveFilters } from '@components/filters/filter-utils';

import {
  DEFAULT_ASSETS_SORT,
  useGetVersionPackagesSummaryQuery,
  useGetVersionPolicyStatusQuery,
  useGetVersionVulnerabilitySummaryQuery,
  useListAssetsInfiniteQuery,
  useListVersionContentsInfiniteQuery,
  useListVersionPolicyFindingsInfiniteQuery,
  useListVersionVulnerabilitiesInfiniteQuery,
} from '../applications.api';
import {
  type ApplicationDetailTab,
  COMPLIANCE_SORT,
  CONTENTS_SORT,
  DEFAULT_VULNERABILITY_FILTERS,
  VULNERABILITIES_SORT,
} from '../applications.const';
import type {
  AssetsListFilters,
  ComplianceFilters,
  ContentsListFilters,
  PackagesSummary,
  VulnerabilitiesFilters,
  VulnerabilitySummary,
} from '../applications.models';

import { applicationVersionPolicyTag } from './cache-tags';

/**
 * 409 from `/policy-findings` or `/policy-status` means a policy-eval
 * job is in flight for the version; the call site treats it as a wait
 * state, not an error.
 */
export const isPolicyEvalConflict = (error: unknown): boolean =>
  !!error &&
  typeof error === 'object' &&
  'status' in error &&
  error.status === 409;

export const POLICY_EVAL_POLL_MS = 3_000;

// Page-level and tab-body call sites both go through these wrappers
// so RTK Query cache keys stay aligned at a single source of truth.

type VersionTabQueryOptions = { skip?: boolean };
type SortableVersionTabQueryOptions = VersionTabQueryOptions & {
  sort?: PaginationSort;
};

export const useVersionAssetsQuery = (
  appId: string,
  versionId: string,
  {
    filters,
    sort = DEFAULT_ASSETS_SORT,
    skip,
  }: SortableVersionTabQueryOptions & { filters?: AssetsListFilters } = {},
) =>
  useListAssetsInfiniteQuery(
    {
      appId,
      sort,
      versionId,
      ...(filters && hasActiveFilters(filters) && { filters }),
    },
    { skip },
  );

export const useVersionComplianceQuery = (
  appId: string,
  versionId: string,
  {
    filters,
    sort = COMPLIANCE_SORT.fallback,
    skip,
    pollingInterval,
  }: SortableVersionTabQueryOptions & {
    filters?: ComplianceFilters;
    pollingInterval?: number;
  } = {},
) =>
  useListVersionPolicyFindingsInfiniteQuery(
    { appId, filters, sort, versionId },
    { skip, pollingInterval },
  );

export const useVersionContentsQuery = (
  appId: string,
  versionId: string,
  {
    filters,
    sort = CONTENTS_SORT.fallback,
    skip,
  }: SortableVersionTabQueryOptions & { filters?: ContentsListFilters } = {},
) =>
  useListVersionContentsInfiniteQuery(
    {
      appId,
      sort,
      versionId,
      ...(filters && hasActiveFilters(filters) && { filters }),
    },
    { skip },
  );

export const useVersionVulnerabilitiesQuery = (
  appId: string,
  versionId: string,
  {
    filters,
    sort = VULNERABILITIES_SORT.fallback,
    skip,
  }: SortableVersionTabQueryOptions & { filters?: VulnerabilitiesFilters } = {},
) =>
  useListVersionVulnerabilitiesInfiniteQuery(
    { appId, filters, sort, versionId },
    { skip },
  );

/**
 * Empty filter objects are normalized out of the cache key so the
 * page-level summary shares a request with the tab body's empty-
 * filter render.
 */
export const useVersionPackagesSummaryQuery = (
  appId: string,
  versionId: string,
  {
    filters,
    skip,
  }: VersionTabQueryOptions & { filters?: ContentsListFilters } = {},
) =>
  useGetVersionPackagesSummaryQuery(
    {
      appId,
      versionId,
      ...(filters && hasActiveFilters(filters) && { filters }),
    },
    { skip },
  );

/**
 * Empty filter objects are normalized out of the cache key so the
 * page-level summary shares a request with the tab body's empty-
 * filter render.
 */
export const useVersionVulnerabilitySummaryQuery = (
  appId: string,
  versionId: string,
  {
    filters,
    skip,
  }: VersionTabQueryOptions & { filters?: VulnerabilitiesFilters } = {},
) =>
  useGetVersionVulnerabilitySummaryQuery(
    {
      appId,
      versionId,
      ...(filters && hasActiveFilters(filters) && { filters }),
    },
    { skip },
  );

/**
 * Non-Summary tab queries (and their metric summaries) fire only once
 * `hasAssets` is confirmed true — the active tab so its body subscribes
 * to a warm in-flight request, the inactive tabs as prefetch for later
 * switches. A version with no assets fetches nothing here: the page
 * redirects such navigations back to Summary, so firing the active tab's
 * query would be a wasted call against the soon-to-unmount tab.
 * Nothing here gates the page spinner — each tab body renders metrics
 * with placeholders and shows its own loader for the table area.
 *
 * `hasAssets` is an input (not computed here) because the page needs
 * it earlier — to drive the sub-nav items' disabled state — before
 * `activeTab` is known.
 */
export const useApplicationVersionTabQueries = ({
  appId,
  versionId,
  activeTab,
  hasAssets,
}: {
  appId: string;
  versionId: string;
  activeTab: ApplicationDetailTab | null;
  hasAssets: boolean;
}) => {
  const dispatch = useAppDispatch();

  const [policyEvalPollingInterval, setPolicyEvalPollingInterval] = useState(0);

  // The active compliance tab fetches with its own URL-derived filters and
  // owns its polling-based recovery; prefetching here would fire a duplicate
  // unfiltered search. Prefetch only to warm it — and to act as the findings
  // 409 sentinel — while another tab is active. `policyStatusQuery` stays
  // unskipped so the cross-tab eval indicator keeps polling regardless.
  const skipCompliancePrefetch = !hasAssets || activeTab === 'compliance';
  const complianceQuery = useVersionComplianceQuery(appId, versionId, {
    skip: skipCompliancePrefetch,
    pollingInterval: policyEvalPollingInterval,
  });
  const policyStatusQuery = useGetVersionPolicyStatusQuery(
    { appId, versionId },
    { skip: !hasAssets, pollingInterval: policyEvalPollingInterval },
  );

  // Poll + invalidate whenever either endpoint conflicts; they don't always
  // flip together.
  const isPolicyEvaluating =
    isPolicyEvalConflict(complianceQuery.error) ||
    isPolicyEvalConflict(policyStatusQuery.error);

  useEffect(() => {
    setPolicyEvalPollingInterval(isPolicyEvaluating ? POLICY_EVAL_POLL_MS : 0);
  }, [isPolicyEvaluating]);

  const wasConflictRef = useRef(false);
  // Navigating to a different version starts a fresh conflict window;
  // clear the latch so we don't invalidate the new version's caches on
  // the carry-over of the previous version's conflict state.
  useEffect(() => {
    wasConflictRef.current = false;
  }, [appId, versionId]);
  // Conflict → no-conflict means the eval job has left its in-flight state.
  // A single invalidation refreshes every sibling sharing
  // `applicationVersionPolicyTag` (policy-status, tab-body findings with
  // other filters) in lockstep. Benign on a transient non-409: siblings
  // refetch and converge.
  useEffect(() => {
    if (wasConflictRef.current && !isPolicyEvaluating) {
      dispatch(
        api.util.invalidateTags([
          applicationVersionPolicyTag(appId, versionId),
        ]),
      );
    }
    wasConflictRef.current = isPolicyEvaluating;
  }, [appId, dispatch, isPolicyEvaluating, versionId]);

  // The active contents/vuln tabs fetch with their own URL-derived filters;
  // prefetching them here would just duplicate that with defaults. Prefetch
  // only to warm a tab while another is active.
  const skipContentsPrefetch = !hasAssets || activeTab === 'contents';
  const contentsQuery = useVersionContentsQuery(appId, versionId, {
    skip: skipContentsPrefetch,
  });
  const skipVulnerabilitiesPrefetch =
    !hasAssets || activeTab === 'vulnerabilities';
  const vulnerabilitiesQuery = useVersionVulnerabilitiesQuery(
    appId,
    versionId,
    {
      filters: DEFAULT_VULNERABILITY_FILTERS,
      skip: skipVulnerabilitiesPrefetch,
    },
  );
  /**
   * Vulnerability summary is intentionally not part of `activeTabLoading`:
   * at scale the summary can take much longer than findings, and we
   * show placeholder metric cards until it lands.
   */
  useVersionVulnerabilitySummaryQuery(appId, versionId, {
    filters: DEFAULT_VULNERABILITY_FILTERS,
    skip: skipVulnerabilitiesPrefetch,
  });

  const activeTabLoading =
    (activeTab === 'compliance' && complianceQuery.isLoading) ||
    (activeTab === 'contents' && contentsQuery.isLoading) ||
    (activeTab === 'vulnerabilities' && vulnerabilitiesQuery.isLoading);

  return {
    complianceQuery,
    contentsQuery,
    vulnerabilitiesQuery,
    activeTabLoading,
    isPolicyEvaluating,
  };
};

/**
 * Returns full summary objects (not just totals) so the Summary tab
 * can consume the same data without a duplicate fetch.
 */
export const useApplicationVersionPageSummaries = (
  appId: string,
  versionId: string,
  { skip }: VersionTabQueryOptions = {},
): {
  contents: PackagesSummary | undefined;
  contentsError: FetchBaseQueryError | SerializedError | undefined;
  contentsLoading: boolean;
  contentsFetching: boolean;
  contentsRefetch: () => void;
  vulnerabilities: VulnerabilitySummary | undefined;
  vulnerabilitiesError: FetchBaseQueryError | SerializedError | undefined;
  vulnerabilitiesLoading: boolean;
  vulnerabilitiesFetching: boolean;
  vulnerabilitiesRefetch: () => void;
} => {
  const {
    data: contents,
    error: contentsError,
    isLoading: contentsLoading,
    isFetching: contentsFetching,
    refetch: contentsRefetch,
  } = useVersionPackagesSummaryQuery(appId, versionId, { skip });
  const {
    data: vulnerabilities,
    error: vulnerabilitiesError,
    isLoading: vulnerabilitiesLoading,
    isFetching: vulnerabilitiesFetching,
    refetch: vulnerabilitiesRefetch,
  } = useVersionVulnerabilitySummaryQuery(appId, versionId, { skip });

  return {
    contents,
    contentsError,
    contentsLoading,
    contentsFetching,
    contentsRefetch,
    vulnerabilities,
    vulnerabilitiesError,
    vulnerabilitiesLoading,
    vulnerabilitiesFetching,
    vulnerabilitiesRefetch,
  };
};
